import { formatCurrency } from "@angular/common";
import { Component, Input, LOCALE_ID, OnInit, inject } from "@angular/core";
import { isErroredExecutionResult, isHttpResponseError, ToastService } from "@tylertech/core-module";
import { getLoggerFor } from "@tylertech/logging";
import { MetadataActionService, RepositoryFormData } from "@tylertech/metadata-module";
import { IInBalanceCheck } from "@tylertech/tyler-api-types";
import { InBalanceManagerService, InBalanceService } from "../../services";

@Component({
    selector: "in-balance-check",
    templateUrl: "./inBalanceCheck.tmpl.html",
    styleUrls: ["./inBalanceCheck.component.scss"],
    standalone: false
})
export class InBalanceCheckComponent implements OnInit
{
    private readonly $toastService = inject(ToastService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private readonly $inBalanceService = inject(InBalanceService);
    private readonly $inBalanceManagerService = inject(InBalanceManagerService);
    private readonly locale = inject(LOCALE_ID);

    @Input() provider: string;
    @Input() index: number;

    private readonly log = getLoggerFor("InBalanceCheckComponent");
    check: IInBalanceCheck;
    loaded: boolean = false;
    hasAction: boolean;

    ngOnInit(): void
    {
        this.$inBalanceService.GetInBalanceCheck(this.provider)
            .then(
                (result: IInBalanceCheck) =>
                {
                    this.check = result;
                    this.hasAction = !!this.check?.ViewAction;
                    this.$inBalanceManagerService.AddToSort(result);
                },
                (error: unknown) =>
                {
                    if (isErroredExecutionResult(error))
                    {
                        this.$toastService.showToast(error.ErrorMessage);
                    }
                    else if (isHttpResponseError(error))
                    {
                        this.$toastService.showToast(error.error.Message);
                    }
                    else
                    {
                        this.$toastService.showToast("An Error occurred.");
                        this.log.error(`An Error occurred: ${error}`, new Error(`An Error occurred.`));
                    }
                })
            .finally(() =>
            {
                this.loaded = true;
            });
    }

    view(): void
    {
        this.$metadataActionService.ExecuteAction(null, this.check.ViewAction, new RepositoryFormData(null, null));
    }

    get name(): string { return this.check?.Name || ""; }

    get variance(): string
    {
        if (!this.check?.Variance)
        {
            return "";
        }

        const variance = this.check.IsCurrency
            ? formatCurrency(this.check.Variance, this.locale, "$")
            : this.check.Variance;
        return `Variance: ${variance}`;
    }

    get icon(): string { return this.hasVariance ? "error" : "check_circle"; }

    get iconStyle(): Record<string, string>
    {
        return {
            'color': this.hasVariance
                ? "var(--forge-theme-warning)"
                : "var(--forge-theme-success)"
        };
    }

    private get hasVariance(): boolean { return this.check?.Variance > 0; }

    get showCheck(): boolean { return !!this.check && this.check.IsHidden !== true; }
}
